import {ICONS} from "@/assets/icons";
import {VOrderSectionEnum} from "@/gql/gen";
import {useFortuneStore} from "@/modules/fortune/store/fortune";
import {useBasketRulesStore} from "@/modules/shop/store/basket-rules";
import {useLoalityStore} from "@/modules/shop/store/loyalty";
import {storeToRefs} from "pinia";
import {computed} from "vue";
import {useVorderPrize} from "@/modules/shop/store/vorder-prize";
import {useVorderCoupon} from "@/modules/shop/store/vorder-coupon";

/**
 * Опция типа льготы для выбора пользователем
 */
export type BenefitTypeOption = {
    nameTab: string;
    nameShort: string;
    name: string;
    value: string;
    cellValue?: string | number | null;
    icon: any;
    useLabel: string;
    section: VOrderSectionEnum;
};

/**
 * Composable для получения доступных типов льгот
 *
 * Объединяет данные из:
 * - basket-rules (discount, gift)
 * - loyalty (bonus)
 * - fortune (prize)
 * - coupon (всегда доступен)
 */
export function useBenefitTypes() {
    const basketRulesStore = useBasketRulesStore();
    const loyaltyStore = useLoalityStore();
    const fortuneStore = useFortuneStore();
    const vorderPrize = useVorderPrize()
    const vorderCoupon = useVorderCoupon()

    const {isAvailableBenefitType} = basketRulesStore
    const {discountBenefitsStandaloneBest} = storeToRefs(basketRulesStore);

    /**
     * Доступные типы льгот для выбора пользователем
     */
    const benefitTypes = computed<BenefitTypeOption[]>(() => {
        const result: BenefitTypeOption[] = [];

        // Приз (из fortune store)
        if (isAvailableBenefitType('prize')) {
            result.push({
                nameTab: 'Приз',
                nameShort: 'Приз',
                name: 'Приз',
                value: 'prize',
                icon: ICONS.percent,
                useLabel: 'Будет использован приз',
                section: 'benefit_prize',
            });
        }

        // Скидка (из basket rules)
        if (isAvailableBenefitType('discount') && discountBenefitsStandaloneBest.value) {
            result.push({
                nameTab: 'Скидка',
                nameShort: discountBenefitsStandaloneBest.value.rule.NAME!,
                name: `${discountBenefitsStandaloneBest.value.rule.NAME} ${discountBenefitsStandaloneBest.value.DISCOUNT_PERCENT}%`,
                cellValue: discountBenefitsStandaloneBest.value.DISCOUNT_PERCENT + '%',
                value: 'discount',
                icon: ICONS.percent,
                useLabel: 'Будет использована',
                section: 'benefit_discount',
            });
        }

        // Подарок (из basket rules)
        if (isAvailableBenefitType('gift')) {
            result.push({
                nameTab: 'Подарок',
                nameShort: 'Подарок',
                name: 'Подарок',
                value: 'gift',
                icon: 'card_giftcard', // Material Icons
                useLabel: 'Будет использован',
                section: 'benefit_gift', // Используем общую секцию benefit
            });
        }

        // Бонусы (из loyalty store)
        /*
        if (isAvailableBenefitType('bonus')) {
            result.push({
                nameTab: 'Бонусы',
                nameShort: 'Бонусы',
                name: 'Бонусы',
                value: 'bonus',
                icon: 'bonus',
                useLabel: 'Будут использованы',
                section: 'benefit_bonus',
            });
        }

         */

        // Промокод (всегда доступен)
        result.push({
            nameTab: 'Промокод',
            nameShort: 'Промокод',
            name: 'Промокод',
            value: 'coupon',
            icon: 'promocode',
            useLabel: 'Будет использован',
            section: 'benefit_coupon',
        });

        return result;
    });

    /**
     * Информация о текущем выбранном типе льготы
     */
    const getBenefitTypeInfo = (benefitType: string | null) => {
        return benefitTypes.value.find(item => item.value === benefitType);
    };

    return {
        benefitTypes,
        getBenefitTypeInfo,
    };
}
